/**
 * ReceiptStudioPage — iter-289 (Feb 2026)
 * =======================================
 * Single page that lets the admin:
 *   1. See the current CGC (customer) + CGB (merchant) receipt-number
 *      sequences and the NEXT reference that will be issued.
 *   2. Edit the unified brand (logo, accent colors, footer contact info).
 *   3. Preview a sample receipt rendered with the live settings.
 *
 * Route: /dashboard/finance/settings/receipt-studio
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Loader2, Receipt, Palette, Hash, Globe, Phone, Mail,
  Save, RotateCcw, Eye,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReceiptStudioPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [brand, setBrand] = useState(null);
  const [dirty, setDirty] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/receipt-studio/status`,
                                 { headers: getAuthHeaders() });
      setStatus(r.data);
      setBrand(r.data.brand);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const update = (patch) => {
    setBrand((prev) => ({ ...(prev || {}), ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (!dirty || !brand) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/receipt-studio/brand`, brand,
                                 { headers: getAuthHeaders() });
      setBrand(r.data.data);
      setDirty(false);
      toast.success(t('تم حفظ الإعدادات', 'Settings saved'));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const resetFromServer = () => { loadStatus(); };

  const breadcrumb = useMemo(() => [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
    { label_ar: 'الإعدادات', label_en: 'Settings', path: '/dashboard/finance/settings' },
    { label_ar: 'ستوديو الإيصالات', label_en: 'Receipt Studio', path: null },
  ], []);

  return (
    <div className="min-h-screen bg-[#030303] text-white" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#F5A55A]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/settings" />
      <div className="border-b border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>

      <main className="relative z-10 py-6 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-[#F5A55A]/15 border border-[#F5A55A]/30 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-[#F5A55A]" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold" data-testid="receipt-studio-title">
                  {t('ستوديو الإيصالات الموحّد', 'Unified Receipt Studio')}
                </h1>
                <p className="text-[11px] text-white/50 mt-0.5">
                  {t(
                    'شعار وألوان وتذييل موحّد لكل الإيصالات (CGC للعملاء — CGB للتجار)',
                    'One brand across every receipt (CGC for customers, CGB for merchants)',
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {dirty && (
                <Button onClick={resetFromServer} variant="outline" size="sm"
                        className="border-white/10 text-white/70 hover:bg-white/5" data-testid="receipt-studio-reset">
                  <RotateCcw className="h-3.5 w-3.5 me-1.5" />
                  {t('استعادة', 'Discard')}
                </Button>
              )}
              <Button onClick={save} disabled={!dirty || saving} size="sm"
                      className="bg-[#F5A55A] hover:bg-[#d97706] text-black font-semibold" data-testid="receipt-studio-save">
                {saving ? <Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 me-1.5" />}
                {t('حفظ التعديلات', 'Save Changes')}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl bg-white/[0.02] border border-white/10 p-5 h-56 animate-pulse" />
              ))}
            </div>
          ) : !brand ? null : (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* ──── 1) Numbering Status ──── */}
              <section className="lg:col-span-3 bg-white/[0.02] border border-white/10 rounded-2xl p-5"
                       data-testid="receipt-studio-numbering">
                <div className="flex items-center gap-2 mb-4">
                  <Hash className="h-4 w-4 text-[#F5A55A]" />
                  <h3 className="text-sm font-semibold">{t('حالة الترقيم', 'Numbering Status')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SequenceCard
                    label={t('إيصالات منصة التجار', 'Merchant Platform Receipts')}
                    data={status?.merchant}
                    tint="#d4af37"
                    isAr={isAr}
                    testId="seq-cgb"
                  />
                  <SequenceCard
                    label={t('إيصالات منصة العملاء', 'Customer Platform Receipts')}
                    data={status?.customer}
                    tint="#10b981"
                    isAr={isAr}
                    testId="seq-cgc"
                  />
                </div>
                <p className="mt-3 text-[11px] text-white/50 leading-relaxed">
                  {t(
                    `${status?.total_receipts ?? 0} إيصال صدر إجمالاً. الترقيم تلقائي ولا يُسمح بالتعديل اليدوي.`,
                    `${status?.total_receipts ?? 0} total receipts issued. Numbering is automatic and cannot be manually edited.`,
                  )}
                </p>
              </section>

              {/* ──── 2) Brand Settings ──── */}
              <section className="lg:col-span-2 bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-5"
                       data-testid="receipt-studio-brand">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-[#F5A55A]" />
                  <h3 className="text-sm font-semibold">{t('الهوية البصرية', 'Brand Identity')}</h3>
                </div>

                {/* Logo URL */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-white/60">{t('رابط الشعار', 'Logo URL')}</Label>
                  <Input value={brand.logo_url || ''}
                         onChange={(e) => update({ logo_url: e.target.value })}
                         placeholder="/pdf-logo-cloudgm.png"
                         dir="ltr"
                         className="bg-black/30 border-white/10 text-[13px]"
                         data-testid="receipt-studio-logo-url" />
                </div>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-white/60">{t('اللون الأساسي', 'Primary color')}</Label>
                    <div className="flex gap-2">
                      <input type="color" value={brand.accent_from || '#3B9EDC'}
                             onChange={(e) => update({ accent_from: e.target.value })}
                             className="h-9 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                             data-testid="receipt-studio-color-from" />
                      <Input value={brand.accent_from || ''}
                             onChange={(e) => update({ accent_from: e.target.value })}
                             dir="ltr"
                             className="bg-black/30 border-white/10 text-[13px] flex-1" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-white/60">{t('اللون الثانوي', 'Secondary color')}</Label>
                    <div className="flex gap-2">
                      <input type="color" value={brand.accent_to || '#F5A55A'}
                             onChange={(e) => update({ accent_to: e.target.value })}
                             className="h-9 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                             data-testid="receipt-studio-color-to" />
                      <Input value={brand.accent_to || ''}
                             onChange={(e) => update({ accent_to: e.target.value })}
                             dir="ltr"
                             className="bg-black/30 border-white/10 text-[13px] flex-1" />
                    </div>
                  </div>
                </div>

                {/* Brand name AR / EN */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-white/60">{t('اسم العلامة (عربي)', 'Brand name (Arabic)')}</Label>
                    <Input value={brand.brand_name_ar || ''}
                           onChange={(e) => update({ brand_name_ar: e.target.value })}
                           className="bg-black/30 border-white/10 text-[13px]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-white/60">{t('اسم العلامة (إنجليزي)', 'Brand name (English)')}</Label>
                    <Input value={brand.brand_name_en || ''}
                           onChange={(e) => update({ brand_name_en: e.target.value })}
                           dir="ltr"
                           className="bg-black/30 border-white/10 text-[13px]" />
                  </div>
                </div>

                {/* Footer contact */}
                <div className="pt-3 border-t border-white/5">
                  <h4 className="text-[11px] uppercase text-white/40 mb-3">{t('تذييل الإيصال', 'Receipt Footer')}</h4>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-[11px] text-white/60 flex items-center gap-1.5">
                        <Globe className="h-3 w-3" /> {t('المواقع (مفصولة بفاصلة)', 'Websites (comma-separated)')}
                      </Label>
                      <Input value={(brand.footer_sites || []).join(', ')}
                             onChange={(e) => update({ footer_sites: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                             dir="ltr"
                             className="bg-black/30 border-white/10 text-[13px]"
                             data-testid="receipt-studio-footer-sites" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] text-white/60 flex items-center gap-1.5">
                        <Phone className="h-3 w-3" /> {t('رقم الهاتف', 'Phone')}
                      </Label>
                      <Input value={brand.footer_phone || ''}
                             onChange={(e) => update({ footer_phone: e.target.value })}
                             dir="ltr"
                             className="bg-black/30 border-white/10 text-[13px]"
                             data-testid="receipt-studio-footer-phone" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] text-white/60 flex items-center gap-1.5">
                        <Mail className="h-3 w-3" /> {t('البُرُد (مفصولة بفاصلة)', 'Emails (comma-separated)')}
                      </Label>
                      <Input value={(brand.footer_emails || []).join(', ')}
                             onChange={(e) => update({ footer_emails: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                             dir="ltr"
                             className="bg-black/30 border-white/10 text-[13px]"
                             data-testid="receipt-studio-footer-emails" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[11px] text-white/60">{t('شكر (عربي)', 'Tagline (Arabic)')}</Label>
                        <Input value={brand.footer_tagline_ar || ''}
                               onChange={(e) => update({ footer_tagline_ar: e.target.value })}
                               className="bg-black/30 border-white/10 text-[13px]" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[11px] text-white/60">{t('شكر (إنجليزي)', 'Tagline (English)')}</Label>
                        <Input value={brand.footer_tagline_en || ''}
                               onChange={(e) => update({ footer_tagline_en: e.target.value })}
                               dir="ltr"
                               className="bg-black/30 border-white/10 text-[13px]" />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* ──── 3) Live Preview ──── */}
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
                       data-testid="receipt-studio-preview">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="h-4 w-4 text-[#F5A55A]" />
                  <h3 className="text-sm font-semibold">{t('معاينة مباشرة', 'Live Preview')}</h3>
                </div>
                <ReceiptPreview brand={brand} isAr={isAr} />
                <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
                  {t(
                    'المعاينة تعرض الهوية فقط. الإيصالات الفعلية تضيف TRN تلقائياً من إعدادات الشركة والشهادة الضريبية.',
                    'Preview shows the brand only. Actual receipts auto-stamp the TRN from company settings and the active tax certificate.',
                  )}
                </p>
              </section>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ────────── Primitives ────────── */

const SequenceCard = ({ label, data, tint, isAr, testId }) => {
  if (!data) return null;
  return (
    <div className="rounded-xl border p-4" style={{
      backgroundColor: `${tint}0A`, borderColor: `${tint}33`,
    }} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-white/50 mb-2">{label}</p>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] text-white/50">{isAr ? 'الرقم التالي' : 'Next number'}</p>
          <p className="text-base font-mono font-bold mt-0.5" style={{ color: tint }} dir="ltr">
            {data.next_number}
          </p>
        </div>
        <div className="text-end">
          <p className="text-[10px] text-white/50">{isAr ? 'آخر رقم صدر' : 'Last issued'}</p>
          <p className="text-sm font-semibold tabular-nums mt-0.5" dir="ltr">
            {data.last_issued > 0 ? `${data.prefix}-${data.year}-${String(data.last_issued).padStart(7, '0')}` : '—'}
          </p>
        </div>
      </div>
      {data.seed > 0 && data.last_issued === 0 && (
        <p className="mt-2 text-[10px] text-white/40">
          {isAr
            ? `البداية مُهيّأة عند الرقم ${data.seed} (مطابقة لمحاسبتكم اليدوية).`
            : `Seeded at ${data.seed} to match legacy accounting.`}
        </p>
      )}
    </div>
  );
};

const ReceiptPreview = ({ brand, isAr }) => {
  const from = brand.accent_from || '#3B9EDC';
  const to   = brand.accent_to   || '#F5A55A';
  const t = (ar, en) => (isAr ? ar : en);
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-white text-slate-800">
      {/* Header strip with the gradient */}
      <div style={{ background: `linear-gradient(90deg, ${from} 0%, ${to} 100%)`, height: 6 }} />
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          {brand.logo_url && (
            <img src={brand.logo_url} alt="" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-slate-900 truncate">
              {isAr ? brand.brand_name_ar : brand.brand_name_en}
            </p>
            <p className="text-[9px] text-slate-500 font-mono truncate" dir="ltr">
              CGB-2026-0000250
            </p>
          </div>
          <div className="text-end">
            <p className="text-[9px] text-slate-500 uppercase">{t('إيصال قبض', 'Payment Receipt')}</p>
            <p className="text-[9px] text-slate-500">
              {new Date().toLocaleDateString(isAr ? 'ar-AE' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="rounded-lg p-3 border-2" style={{ borderColor: to, backgroundColor: `${to}0D` }}>
          <p className="text-[9px] text-slate-500 uppercase">{t('المبلغ المستلم', 'Amount received')}</p>
          <p className="text-xl font-bold" style={{ color: from }} dir="ltr">1,250.00 AED</p>
          <p className="text-[9px] text-slate-500 mt-0.5">{t('مسدَّد بالكامل', 'Paid in full')}</p>
        </div>

        <div className="mt-4 pt-3 border-t border-slate-200 text-[9px] text-slate-500 space-y-0.5">
          <p className="font-semibold" style={{ color: from }}>
            {isAr ? brand.footer_tagline_ar : brand.footer_tagline_en}
          </p>
          <p dir="ltr">{(brand.footer_sites || []).join(' · ')}</p>
          <p dir="ltr">{brand.footer_phone}</p>
          <p dir="ltr">{(brand.footer_emails || []).join(' · ')}</p>
        </div>
      </div>
    </div>
  );
};
